import { useState, useEffect, useCallback } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface AuthState {
  session: Session | null;
  loading: boolean;
  error: string | null;
  signOut: () => Promise<void>;
}

/**
 * Returns the current Supabase session and a signOut helper.
 * `loading` is true until the initial session check resolves.
 * `error` is set if the initial session check fails (e.g. network error).
 */
export function useAuth(): AuthState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Safety timeout: if Supabase never resolves (offline / misconfigured), stop
    // showing the loading screen after 8 seconds and treat as signed-out.
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 8_000);

    supabase.auth
      .getSession()
      .then(({ data, error: err }) => {
        clearTimeout(timeout);
        if (err) {
          setError(err.message);
        } else {
          setSession(data.session);
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        clearTimeout(timeout);
        setError(err instanceof Error ? err.message : 'Failed to load session');
        setLoading(false);
      });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setLoading(false);
      setError(null);
    });

    return () => {
      clearTimeout(timeout);
      listener.subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    await supabase.auth.signOut();
  }, []);

  return { session, loading, error, signOut };
}
